import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import FindRow, { CATEGORIES } from "../../components/FindRow/FindRow";
import { getSaved, deleteSong, updateSong } from "../../api/saved";
import { apiError } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import "./MyFinds.scss";

export default function MyFinds() {
  const toast = useToast();
  const [saved, setSaved] = useState([]);
  const [iframeApi, setIframeApi] = useState();
  const [status, setStatus] = useState("loading"); // loading | done | error
  const [error, setError] = useState("");
  const [activeCat, setActiveCat] = useState("All");

  useEffect(() => {
    window.onSpotifyIframeApiReady = (api) => setIframeApi(api);
  }, []);

  useEffect(() => {
    let active = true;
    getSaved()
      .then((data) => {
        if (!active) return;
        setSaved(data);
        setStatus("done");
      })
      .catch((err) => {
        if (!active) return;
        setError(apiError(err, "Could not load your finds"));
        setStatus("error");
      });
    return () => {
      active = false;
    };
  }, []);

  const catOf = (s) => (CATEGORIES.includes(s.category) ? s.category : "Uncategorized");

  // Category tabs present in the collection, in preset order.
  const tabs = useMemo(() => {
    const counts = {};
    saved.forEach((s) => {
      const c = catOf(s);
      counts[c] = (counts[c] || 0) + 1;
    });
    const present = CATEGORIES.filter((c) => counts[c]);
    return [{ name: "All", count: saved.length }, ...present.map((c) => ({ name: c, count: counts[c] }))];
  }, [saved]);

  const visible = activeCat === "All" ? saved : saved.filter((s) => catOf(s) === activeCat);

  const handleDelete = async (song) => {
    const prev = saved;
    setSaved((list) => list.filter((s) => s.id !== song.id)); // optimistic
    try {
      await deleteSong(song.id);
      toast("Removed from your finds");
    } catch (err) {
      setSaved(prev);
      toast(apiError(err, "Couldn't remove that"), "error");
    }
  };

  const handleCategoryChange = async (song, category) => {
    setSaved((list) => list.map((s) => (s.id === song.id ? { ...s, category } : s)));
    try {
      await updateSong(song.id, { category });
      toast(`Moved to ${category}`);
    } catch (err) {
      toast(apiError(err, "Couldn't move that"), "error");
    }
  };

  return (
    <div className="myfinds">
      <Header />
      <main className="myfinds__main">
        <header className="myfinds__intro rise-1">
          <p className="myfinds__eyebrow"><span>★</span> <i /> Your collection</p>
          <h1 className="myfinds__title">
            My saved <span className="myfinds__title-accent">finds</span>
          </h1>
        </header>

        {status === "loading" && (
          <div className="myfinds__loading">
            <span className="skeleton myfinds__loading-row" />
            <span className="skeleton myfinds__loading-row" />
            <span className="skeleton myfinds__loading-row" />
          </div>
        )}

        {status === "error" && <p className="myfinds__message">{error}</p>}

        {status === "done" && saved.length === 0 && (
          <div className="myfinds__empty">
            <p className="myfinds__message">You haven't saved any finds yet.</p>
            <Link to="/quiz" className="myfinds__cta">Discover something</Link>
          </div>
        )}

        {status === "done" && saved.length > 0 && (
          <>
            <div className="myfinds__tabs" role="tablist">
              {tabs.map((t) => (
                <button
                  key={t.name}
                  role="tab"
                  aria-selected={activeCat === t.name}
                  className={`myfinds__tab${activeCat === t.name ? " is-active" : ""}`}
                  onClick={() => setActiveCat(t.name)}
                >
                  {t.name}
                  <span className="myfinds__tab-count">{t.count}</span>
                </button>
              ))}
            </div>

            <div className="myfinds__list">
              {visible.map((song, i) => (
                <FindRow
                  key={song.id}
                  song={song}
                  index={i}
                  iframeApi={iframeApi}
                  onDelete={handleDelete}
                  onCategoryChange={handleCategoryChange}
                />
              ))}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
